import { trackGenerateLead } from '@/js/lib/analytics';
import { createJsonApiClient } from '@/js/lib/http/create-json-api-client';
import { closeFancybox, openInlineFancybox } from '@/js/modules/fancybox';

const FORM_SELECTOR = '.js-website-form';
const TEMPLATE_SELECTOR = '.js-website-form-success-modal';
const ERROR_SELECTOR = '.js-website-form-error';
const DEFAULT_ERROR_MESSAGE = 'Something went wrong. Please try again.';

function setFormError(form: HTMLFormElement, message: string | null): void {
  const element = form.querySelector<HTMLElement>(ERROR_SELECTOR);

  if (element == null) {
    return;
  }

  if (message == null) {
    element.hidden = true;
    element.textContent = '';
    return;
  }

  element.textContent = message;
  element.hidden = false;
}

const http = createJsonApiClient({});

function getSuccessTemplate(form: HTMLFormElement): HTMLTemplateElement | null {
  const name = form.dataset.successModal;

  if (name == null) {
    return null;
  }

  const templates = document.querySelectorAll<HTMLTemplateElement>(TEMPLATE_SELECTOR);

  for (const template of templates) {
    if (template.dataset.successModal === name && template.content.firstElementChild != null) {
      return template;
    }
  }

  return null;
}

function setSubmitDisabled(
  button: HTMLButtonElement,
  disabled: boolean,
): void {
  button.disabled = disabled;
}

function buildSuccessModal(form: HTMLFormElement): HTMLElement | null {
  const template = getSuccessTemplate(form);

  if (template == null) {
    return null;
  }

  const modal = template.content.firstElementChild!.cloneNode(true) as HTMLElement;
  document.body.appendChild(modal);

  return modal;
}

async function openSuccessModal(form: HTMLFormElement): Promise<void> {
  const modal = buildSuccessModal(form);

  if (modal == null) {
    return;
  }

  await openInlineFancybox(modal, {
    on: {
      destroy: () => {
        modal.remove();
      },
    },
  });
}

async function handleSubmit(event: SubmitEvent): Promise<void> {
  const form = event.currentTarget;

  if (!(form instanceof HTMLFormElement)) {
    return;
  }

  if (!form.reportValidity()) {
    return;
  }

  event.preventDefault();

  const submitButton = form.querySelector<HTMLButtonElement>(
    'button[type="submit"]',
  );

  if (submitButton == null || submitButton.disabled) {
    return;
  }

  setSubmitDisabled(submitButton, true);
  setFormError(form, null);

  let success = false;

  try {
    const { data } = await http.post<{ success: boolean }>(
      form.action,
      new FormData(form),
    );
    success = data.success;
  }
  catch {
    success = false;
  }
  finally {
    setSubmitDisabled(submitButton, false);
  }

  if (!success) {
    setFormError(form, DEFAULT_ERROR_MESSAGE);
    return;
  }

  trackGenerateLead(form.dataset.analyticsPlacement ?? 'page');

  form.reset();

  if (getSuccessTemplate(form) == null) {
    return;
  }

  try {
    await closeFancybox();
    await openSuccessModal(form);
  }
  catch (error) {
    if (import.meta.env.DEV) {
      console.error('[website-form] success modal failed', error);
    }
  }
}

function initWebsiteFormRoot(form: HTMLFormElement): void {
  form.addEventListener('submit', (event) => {
    void handleSubmit(event);
  });
}

export function initWebsiteForms(): void {
  const forms = document.querySelectorAll<HTMLFormElement>(FORM_SELECTOR);

  for (const form of forms) {
    initWebsiteFormRoot(form);
  }
}
